import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend, FunnelChart, Funnel, LabelList,
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;
import { getSalesHeaders as getHeaders } from '../../utils/salesAuth';

function formatMoney(v) {
  if (!v && v !== 0) return '$0';
  if (v >= 1000000) return '$' + (v / 1000000).toFixed(1) + 'M';
  if (v >= 1000) return '$' + (v / 1000).toFixed(1) + 'K';
  return '$' + Math.round(v).toLocaleString();
}

const STAGE_LABELS = {
  new: 'New', contact_attempted: 'Contact Attempted', contacted: 'Contacted',
  qualified: 'Qualified', site_visit: 'Site Visit', estimate_in_progress: 'Est. In Progress',
  estimate_sent: 'Estimate Sent', follow_up: 'Follow Up', customer_decision: 'Decision',
  deposit_requested: 'Deposit Req.', won: 'Won', lost: 'Lost/DQ',
};

const STAGE_COLORS = {
  new: '#3b82f6', contact_attempted: '#6366f1', contacted: '#8b5cf6',
  qualified: '#f59e0b', site_visit: '#f97316', estimate_in_progress: '#ef4444',
  estimate_sent: '#ec4899', follow_up: '#a855f7', customer_decision: '#14b8a6',
  deposit_requested: '#22c55e', won: '#16a34a', lost: '#6b7280',
};

const SOURCE_COLORS = ['#3b82f6', '#f59e0b', '#ef4444', '#22c55e', '#8b5cf6', '#ec4899', '#f97316', '#14b8a6', '#6366f1', '#a855f7', '#64748b', '#0ea5e9'];
const LOST_REASON_COLORS = ['#ef4444', '#f97316', '#f59e0b', '#eab308', '#22c55e', '#14b8a6', '#3b82f6', '#6366f1', '#8b5cf6', '#ec4899'];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: '10px 14px', boxShadow: '0 4px 12px rgba(0,0,0,.3)' }}>
      <p style={{ color: '#94a3b8', fontSize: 12, margin: '0 0 6px', fontWeight: 600 }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color, fontSize: 13, margin: '2px 0' }}>
          {p.name}: <strong>{typeof p.value === 'number' && p.name?.toLowerCase().includes('value') ? formatMoney(p.value) : p.value}</strong>
        </p>
      ))}
    </div>
  );
};

export default function SalesAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/sales/analytics`, { credentials: 'include', headers: getHeaders() })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div data-testid="analytics-loading" style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading analytics...</div>;
  if (!data) return <div style={{ padding: 40, textAlign: 'center', color: '#ef4444' }}>Failed to load analytics</div>;

  const funnelData = (data.funnel || []).filter(f => f.count > 0).map(f => ({
    name: STAGE_LABELS[f.stage] || f.stage,
    value: f.count,
    fill: STAGE_COLORS[f.stage] || '#64748b',
  }));

  const forecastData = (data.forecast || []).map(f => ({
    stage: STAGE_LABELS[f.stage] || f.stage,
    'Raw Value': f.raw_value,
    'Weighted Value': f.weighted_value,
    probability: f.probability,
    count: f.count,
  }));

  const sourceData = (data.sources || []).slice(0, 10).map(s => ({
    name: (s.source || 'unknown').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
    value: s.total,
    won: s.won,
    lost: s.lost,
    open: s.open,
    win_rate: s.win_rate,
  }));

  const sourcePieData = sourceData.map((s, i) => ({ ...s, fill: SOURCE_COLORS[i % SOURCE_COLORS.length] }));

  const lostReasonsData = (data.win_loss?.lost_reasons || []).map((r, i) => ({
    name: (r.reason || 'unknown').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
    value: r.count,
    fill: LOST_REASON_COLORS[i % LOST_REASON_COLORS.length],
  }));

  const trendData = (data.monthly_trends || []).map(m => ({
    month: m.month,
    Created: m.created,
    Won: m.won,
    Lost: m.lost,
  }));

  const stageTimeData = Object.entries(data.stage_times || {})
    .filter(([, v]) => v > 0)
    .map(([stage, days]) => ({
      stage: STAGE_LABELS[stage] || stage,
      days: days,
      fill: STAGE_COLORS[stage] || '#64748b',
    }));

  const activityData = (data.activity_summary || []).slice(0, 8).map(a => ({
    type: (a.type || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
    count: a.count,
  }));

  const wl = data.win_loss || {};

  return (
    <div data-testid="analytics-view" style={{ padding: '24px 28px', maxWidth: 1400 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1e293b', margin: '0 0 20px' }}>Sales Analytics</h1>

      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: 28 }}>
        <KpiCard label="Total Opportunities" value={data.total_opportunities} color="#3b82f6" />
        <KpiCard label="Win Rate" value={`${wl.win_rate}%`} color="#16a34a" sub={`${wl.won} won / ${wl.closed} closed`} />
        <KpiCard label="Avg Deal Size" value={formatMoney(wl.avg_deal_size)} color="#8b5cf6" />
        <KpiCard label="Total Won" value={formatMoney(wl.total_won_value)} color="#22c55e" />
        <KpiCard label="Weighted Pipeline" value={formatMoney(data.forecast_totals?.weighted_pipeline)} color="#f59e0b" sub={`of ${formatMoney(data.forecast_totals?.unweighted_pipeline)} total`} />
      </div>

      {/* Row 1: Funnel + Pipeline Forecast */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 20, marginBottom: 20 }}>
        <ChartCard title="Conversion Funnel" testId="chart-funnel">
          {funnelData.length > 0 ? (
            <ResponsiveContainer width="100%" height={340}>
              <FunnelChart>
                <Tooltip content={<CustomTooltip />} />
                <Funnel dataKey="value" data={funnelData} isAnimationActive>
                  <LabelList position="right" fill="#475569" stroke="none" fontSize={12} dataKey="name" />
                  <LabelList position="center" fill="#fff" stroke="none" fontSize={13} fontWeight="bold" dataKey="value" />
                </Funnel>
              </FunnelChart>
            </ResponsiveContainer>
          ) : <EmptyState />}
        </ChartCard>

        <ChartCard title="Pipeline Forecast (Weighted)" testId="chart-forecast">
          {forecastData.length > 0 ? (
            <ResponsiveContainer width="100%" height={340}>
              <BarChart data={forecastData} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="stage" fontSize={11} tick={{ fill: '#64748b' }} angle={-25} textAnchor="end" height={60} />
                <YAxis fontSize={11} tick={{ fill: '#64748b' }} tickFormatter={v => formatMoney(v)} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="Raw Value" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Weighted Value" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyState />}
        </ChartCard>
      </div>

      {/* Row 2: Lead Sources + Lost Reasons */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        <ChartCard title="Lead Source Distribution" testId="chart-sources">
          {sourcePieData.length > 0 ? (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <ResponsiveContainer width="60%" height={280}>
                <PieChart>
                  <Pie data={sourcePieData} dataKey="value" cx="50%" cy="50%" outerRadius={100} innerRadius={50} paddingAngle={2}>
                    {sourcePieData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ width: '40%', fontSize: 12 }}>
                {sourcePieData.map((s, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 2, background: s.fill, flexShrink: 0 }} />
                    <span style={{ color: '#475569' }}>{s.name}</span>
                    <span style={{ marginLeft: 'auto', fontWeight: 600, color: '#1e293b' }}>{s.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : <EmptyState />}
        </ChartCard>

        <ChartCard title="Lost Reasons" testId="chart-lost-reasons">
          {lostReasonsData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={lostReasonsData} layout="vertical" margin={{ left: 20, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" fontSize={11} tick={{ fill: '#64748b' }} />
                <YAxis type="category" dataKey="name" fontSize={11} tick={{ fill: '#64748b' }} width={120} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" name="Count" radius={[0, 4, 4, 0]}>
                  {lostReasonsData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <NoLostData />}
        </ChartCard>
      </div>

      {/* Row 3: Monthly Trends + Activity Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 20, marginBottom: 20 }}>
        <ChartCard title="Monthly Trends (6 Months)" testId="chart-trends">
          {trendData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={trendData} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" fontSize={11} tick={{ fill: '#64748b' }} />
                <YAxis fontSize={11} tick={{ fill: '#64748b' }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Line type="monotone" dataKey="Created" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} />
                <Line type="monotone" dataKey="Won" stroke="#16a34a" strokeWidth={2} dot={{ r: 4 }} />
                <Line type="monotone" dataKey="Lost" stroke="#ef4444" strokeWidth={2} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : <EmptyState />}
        </ChartCard>

        <ChartCard title="Activity (Last 30 Days)" testId="chart-activity">
          {activityData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={activityData} margin={{ top: 5, right: 10, bottom: 5, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="type" fontSize={10} tick={{ fill: '#64748b' }} angle={-25} textAnchor="end" height={60} />
                <YAxis fontSize={11} tick={{ fill: '#64748b' }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" name="Count" fill="#6366f1" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyState />}
        </ChartCard>
      </div>

      {/* Row 4: Avg Time in Stage + Source Win Rates */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        <ChartCard title="Avg. Days in Stage" testId="chart-stage-time">
          {stageTimeData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={stageTimeData} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="stage" fontSize={10} tick={{ fill: '#64748b' }} angle={-25} textAnchor="end" height={60} />
                <YAxis fontSize={11} tick={{ fill: '#64748b' }} unit="d" />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="days" name="Avg Days" radius={[4, 4, 0, 0]}>
                  {stageTimeData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyState />}
        </ChartCard>

        <ChartCard title="Source Win Rates" testId="chart-source-rates">
          {sourceData.length > 0 ? (
            <div style={{ padding: '0 4px', maxHeight: 280, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                    <th style={{ textAlign: 'left', padding: '8px 10px', color: '#475569', fontWeight: 600, fontSize: 12 }}>Source</th>
                    <th style={{ textAlign: 'center', padding: '8px 6px', color: '#475569', fontWeight: 600, fontSize: 12 }}>Total</th>
                    <th style={{ textAlign: 'center', padding: '8px 6px', color: '#475569', fontWeight: 600, fontSize: 12 }}>Won</th>
                    <th style={{ textAlign: 'center', padding: '8px 6px', color: '#475569', fontWeight: 600, fontSize: 12 }}>Open</th>
                    <th style={{ textAlign: 'right', padding: '8px 10px', color: '#475569', fontWeight: 600, fontSize: 12 }}>Win Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {sourceData.map((s, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '8px 10px', color: '#1e293b', fontWeight: 500 }}>{s.name}</td>
                      <td style={{ textAlign: 'center', padding: '8px 6px', color: '#64748b' }}>{s.value}</td>
                      <td style={{ textAlign: 'center', padding: '8px 6px', color: '#16a34a', fontWeight: 600 }}>{s.won}</td>
                      <td style={{ textAlign: 'center', padding: '8px 6px', color: '#f59e0b' }}>{s.open}</td>
                      <td style={{ textAlign: 'right', padding: '8px 10px' }}>
                        <span style={{
                          background: s.win_rate >= 50 ? '#dcfce7' : s.win_rate >= 25 ? '#fef9c3' : '#fef2f2',
                          color: s.win_rate >= 50 ? '#16a34a' : s.win_rate >= 25 ? '#ca8a04' : '#dc2626',
                          padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600,
                        }}>{s.win_rate}%</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <EmptyState />}
        </ChartCard>
      </div>
    </div>
  );
}

function KpiCard({ label, value, color, sub }) {
  return (
    <div data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}
      style={{ background: '#fff', borderRadius: 10, padding: '16px 18px', borderLeft: `4px solid ${color}`, boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
      <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: '#1e293b', marginTop: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function ChartCard({ title, children, testId }) {
  return (
    <div data-testid={testId} style={{ background: '#fff', borderRadius: 10, padding: '18px 20px', boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
      <h3 style={{ fontSize: 15, fontWeight: 600, color: '#1e293b', margin: '0 0 14px' }}>{title}</h3>
      {children}
    </div>
  );
}

function EmptyState() {
  return <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No data available yet</div>;
}

function NoLostData() {
  return <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No lost deals with recorded reasons</div>;
}
